import {validations} from "../../../utils";

/**
 * Regras do formulário dinâmico (sem React): visibilidade, limpeza e validação das respostas.
 * Ver o formato das perguntas em questionnaireDefinitions.js.
 */

export const REQUIRED_MESSAGE = "O campo é obrigatório.";

export const TERRAIN_FIELDS = [
  "condition", "terrainState", "terrainCity", "propertyArea", "exploredArea", "resources",
];

const isEmpty = (value) =>
  value === undefined || value === null || (typeof value === "string" && value.trim() === "") ||
  (Array.isArray(value) && value.length === 0);

/** Chaves de answers que uma pergunta ocupa. */
export function questionKeys(question) {
  if (question.type === "section") return [];
  if (question.type === "checkText") return question.fields.map((f) => f.name);
  return [question.name];
}

export function isVisible(question, answers) {
  return !question.showIf || Boolean(question.showIf(answers));
}

/** Perguntas a exibir; uma seção só aparece se tiver alguma pergunta visível. */
export function visibleQuestions(questions, answers) {
  const result = [];
  let pendingSection = null;
  questions.forEach((q) => {
    if (q.type === "section") {
      pendingSection = q;
      return;
    }
    if (!isVisible(q, answers)) return;
    if (pendingSection) {
      result.push(pendingSection);
      pendingSection = null;
    }
    result.push(q);
  });
  return result;
}

/** Caixa de um checkText começa marcada se algum dos seus campos tiver valor. */
export function isCheckTextFilled(question, answers) {
  return question.fields.some((f) => !isEmpty(answers[f.name]));
}

/**
 * JSON a gravar: remove as respostas das perguntas escondidas. Chaves que não pertencem a
 * nenhuma pergunta (ex.: vindas do app ou de versões antigas) são mantidas.
 */
export function cleanAnswers(questions, answers) {
  const cleaned = {...answers};
  questions.forEach((q) => {
    if (!isVisible(q, answers)) {
      questionKeys(q).forEach((key) => delete cleaned[key]);
    }
  });
  return cleaned;
}

function isValidDate(value) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value || "");
  if (!m) return false;
  const date = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  return date.getMonth() === Number(m[2]) - 1 && date <= new Date() && date.getFullYear() >= 1900;
}

function formatError(question, value) {
  if (isEmpty(value)) return null;
  switch (question.type) {
    case "date":
      return isValidDate(value) ? null : "Informe uma data válida (dd/mm/aaaa) até hoje.";
    case "year": {
      const year = Number(value);
      return /^\d{4}$/.test(value) && year >= 1900 && year <= new Date().getFullYear()
        ? null
        : "Informe um ano válido (aaaa).";
    }
    case "inss":
      return validations.validateInss(value) || null;
    default:
      return null;
  }
}

/**
 * Valida as perguntas visíveis. Retorna { [chave]: mensagem }.
 * `openChecks` = checkTexts marcados (índice da pergunta -> boolean).
 * Terrenos usam chaves "terrains.<i>.<campo>".
 */
export function validateAnswers(questions, answers, openChecks) {
  const errors = {};
  questions.forEach((q, index) => {
    if (q.type === "section" || !isVisible(q, answers)) return;

    if (q.type === "checkText") {
      if (openChecks[index]) {
        q.fields.forEach((f) => {
          if (isEmpty(answers[f.name])) errors[f.name] = REQUIRED_MESSAGE;
        });
      }
      return;
    }

    if (q.type === "terrains") {
      (answers.terrains || []).forEach((terrain, i) => {
        TERRAIN_FIELDS.forEach((field) => {
          if (isEmpty(terrain[field])) errors[`terrains.${i}.${field}`] = REQUIRED_MESSAGE;
        });
      });
      return;
    }

    const value = answers[q.name];
    const required = typeof q.required === "function" ? q.required(answers) : q.required;
    const error =
      (required && isEmpty(value) ? REQUIRED_MESSAGE : null) ||
      formatError(q, value) ||
      (q.validate ? q.validate(value, answers) : null);
    if (error) errors[q.name] = error;
  });
  return errors;
}
